import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  StudentInfoAttendenceCalender,
  StudentInfoAttendenceDate,
} from "../../../api/StudentServices";
import { ToastMain } from "../../master/Master";
import { holidaysList } from "../../../api/Holidays";

const localizer = momentLocalizer(moment);

const CustomToolbar = (toolbar) => {
  const goToBack = () => toolbar.onNavigate("PREV");
  const goToNext = () => toolbar.onNavigate("NEXT");
  const goToToday = () => toolbar.onNavigate("TODAY");
  const goToView = (view) => toolbar.onView(view);

  return (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div>
        <button className="btn btn-outline-primary me-2" onClick={goToBack}>
          ⬅ Back
        </button>
        <button className="btn btn-outline-primary me-2" onClick={goToToday}>
          📅 Today
        </button>
        <button className="btn btn-outline-primary" onClick={goToNext}>
          Next ➡
        </button>
      </div>
      <h5 className="fw-bold">{toolbar.label}</h5>
      <div>
        <button
          className="btn btn-outline-primary me-2"
          onClick={() => goToView("month")}
        >
          Month
        </button>
        <button
          className="btn btn-outline-primary me-2"
          onClick={() => goToView("week")}
        >
          Week
        </button>
        <button
          className="btn btn-outline-primary"
          onClick={() => goToView("day")}
        >
          Day
        </button>
      </div>
    </div>
  );
};

const AttandanceTab = () => {
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryId = queryParams.get("id");
  const studentId = queryId || id;

  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, "0")}`;
  const [month, setMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [date, setDate] = useState("");
  const [leaves, setLeaves] = useState([]);

  // ✅ Fetch Leaves separately
  const getLeaves = async () => {
    setLoading(true);
    try {
      const res = await holidaysList();
      if (res?.data?.data) {
        setLeaves(res.data.data); // <-- Correct: res.data.data not res.data
        console.log("Fetched leaves", res.data.data);
      }
    } catch (err) {
      ToastMain.fire({
        icon: "error",
        title: err?.response?.data?.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLeaves();
  }, []);

  const DateByData = async () => {
    try {
      const response = await StudentInfoAttendenceDate(studentId, date);
      if (response?.data?.status) {
        setTableData(response.data.data);
      } else {
        ToastMain.fire({
          icon: "info",
          title: response?.data?.message || "Something went wrong",
        });
      }
    } catch (error) {
      ToastMain.fire({
        icon: "error",
        title: error?.response?.data?.message || "Something went wrong",
      });
    }
  };

  useEffect(() => {
    if (studentId && date) {
      DateByData();
    }
  }, [studentId, date]);

  // ✅ Combine Attendance + Leaves properly
  useEffect(() => {
    if (!studentId) return;
    const fetchStudent = async () => {
      setLoading(true);
      try {
        const response = await StudentInfoAttendenceCalender(studentId, month);
        let evts = [];

        // 🔹 Attendance Events
        if (response.status && response.data?.data) {
          response.data.data.forEach((att) => {
            evts.push({
              title: att.check_in === "1" ? "Present" : "Absent",
              start: new Date(att.date),
              end: new Date(att.date),
              allDay: true,
              check_in_time: att.check_in_time,
              check_out_time: att.check_out_time,
              created_at: att.created_at,
            });
          });
        }

        // 🔹 Leaves Events (from separate API)
        if (leaves?.length > 0) {
          leaves.forEach((leave) => {
            const leaveDate = moment(leave.date).toDate();
            evts.push({
              title: "Leave",
              start: leaveDate,
              end: leaveDate,
              allDay: true,
              leaveTitle: leave.title,
              leaveDescription: leave.description,
              created_at: leave.created_at,
            });
          });
        }

        console.log("Final merged events", evts);
        setEvents(evts);
      } catch (err) {
        console.error("API error", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStudent();
  }, [studentId, month, leaves]); // ✅ Depend on leaves

  return (
    <>
      {loading && (
        <div
          className="d-flex justify-content-center align-items-center position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 9999 }}
        >
          <div className="sk-chase sk-primary display-1">
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
          </div>
        </div>
      )}

      <div className="card mb-6 mt-3 shadow-lg rounded-xl">
        <h5 className="card-header bg-label-primary text-white">
          Attendance Calendar
        </h5>

        <div className="card-body mt-3">
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 500 }}
            views={["month", "week", "day"]}
            components={{ toolbar: CustomToolbar }}
            onSelectEvent={(slotInfo, event) => {
              const formatted = slotInfo.start.toISOString().split("T")[0];
              setDate(formatted);
              setSelectedEvent(event);
            }}
            onNavigate={(date) => {
              const year = date.getFullYear();
              const monthStr = String(date.getMonth() + 1).padStart(2, "0");
              const day = String(date.getDate()).padStart(2, "0");
              setMonth(`${year}-${monthStr}`);
              setDate(`${year}-${monthStr}-${day}`);
            }}
            eventPropGetter={(event) => {
              let bg = "#4caf50"; // Present
              if (event.title === "Absent") bg = "#c76b64";
              if (event.title === "Leave") bg = "#ff9800";
              return { style: { backgroundColor: bg, color: "white" } };
            }}
          />
        </div>

        {/* ✅ Attendance/Leave Details */}
        {/* {selectedEvent && (
          <div className="card mt-3 shadow p-3">
            <h5 className="fw-bold">
              {selectedEvent.title === "Leave"
                ? "Leave Details"
                : "Attendance Details"}
            </h5>

            {selectedEvent.title === "Leave" ? (
              <>
                <p>
                  <strong>Date:</strong>{" "}
                  {moment(selectedEvent.start).format("YYYY-MM-DD")}
                </p>
                <p>
                  <strong>Leave Title:</strong>{" "}
                  {selectedEvent.leaveTitle || "N/A"}
                </p>
                <p>
                  <strong>Description:</strong>{" "}
                  {selectedEvent.leaveDescription || "N/A"}
                </p>
              </>
            ) : (
              <>
                <p>
                  <strong>Date:</strong>{" "}
                  {moment(selectedEvent.start).format("YYYY-MM-DD")}
                </p>
                <p>
                  <strong>In Time:</strong> {selectedEvent.check_in_time || "N/A"}
                </p>
                <p>
                  <strong>Out Time:</strong> {selectedEvent.check_out_time || "N/A"}
                </p>
              </>
            )}
          </div>
        )} */}

        {/* ✅ Attendance Table */}
        {selectedEvent && selectedEvent.title !== "Leave" && (
          <div className="card mt-3 shadow p-3">
            <h5 className="fw-bold">Attendance Details</h5>
            <div className="table-responsive">
              <table className="table mt-3">
                <thead className="table-primary text-center">
                  <tr>
                    <th>Date</th>
                    <th>In Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.length > 0 ? (
                    tableData.map((evt) => (
                      <tr key={evt.id}>
                        <td>{evt.date}</td>
                        <td>{evt.check_in_time || "N/A"}</td>
                        <td>
                          {evt.check_in === "1" ? "Present" : "Absent"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center">
                        No attendance records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AttandanceTab;
